/** `YYYY-MM-DD` in the user's local timezone. */
export type ISODate = string
/** `HH:MM`, 24h. */
export type Time = string

/** The six semantic hues from tokens.css. Stored by name so themes can restyle. */
export type Hue = 'clay' | 'amber' | 'sage' | 'iris' | 'rose' | 'teal'
export const HUES: Hue[] = ['clay', 'amber', 'sage', 'iris', 'rose', 'teal']

/* ----------------------------------------------------------------- calendar */

export type EventKind = 'class' | 'study' | 'deadline' | 'audition' | 'personal'

/** `labelKey` is an i18n key, not a display string — see i18n/en.ts. */
export const EVENT_KINDS: { id: EventKind; labelKey: string; hue: Hue }[] = [
  { id: 'class', labelKey: 'kind.class', hue: 'iris' },
  { id: 'study', labelKey: 'kind.study', hue: 'sage' },
  { id: 'deadline', labelKey: 'kind.deadline', hue: 'clay' },
  { id: 'audition', labelKey: 'kind.audition', hue: 'rose' },
  { id: 'personal', labelKey: 'kind.personal', hue: 'teal' },
]

export interface CalEvent {
  id: string
  title: string
  date: ISODate
  start?: Time
  end?: Time
  kind: EventKind
  notes?: string
  done?: boolean
  /** Set when the event was generated from an application deadline. */
  applicationId?: string
}

/* -------------------------------------------------------------------- study */

export interface Subject {
  id: string
  name: string
  hue: Hue
  /** Weekly study target in minutes. */
  targetMins: number
}

export interface Task {
  id: string
  title: string
  subjectId?: string
  due?: ISODate
  done: boolean
  /** 0 = normal, 1 = important. */
  priority: 0 | 1
  createdAt: number
}

export interface StudySession {
  id: string
  subjectId?: string
  /** Epoch ms when the session ended. */
  at: number
  mins: number
}

/* -------------------------------------------------------------- application */

export type AppStatus =
  | 'researching'
  | 'preparing'
  | 'submitted'
  | 'interview'
  | 'offer'
  | 'declined'

export const APP_STATUSES: { id: AppStatus; labelKey: string; hue: Hue }[] = [
  { id: 'researching', labelKey: 'status.researching', hue: 'teal' },
  { id: 'preparing', labelKey: 'status.preparing', hue: 'amber' },
  { id: 'submitted', labelKey: 'status.submitted', hue: 'iris' },
  { id: 'interview', labelKey: 'status.interview', hue: 'rose' },
  { id: 'offer', labelKey: 'status.offer', hue: 'sage' },
  { id: 'declined', labelKey: 'status.declined', hue: 'clay' },
]

export interface Requirement {
  id: string
  label: string
  done: boolean
  due?: ISODate
}

export interface Application {
  id: string
  institution: string
  course: string
  deadline: ISODate
  status: AppStatus
  requirements: Requirement[]
  notes?: string
}

/* ------------------------------------------------------------------ library */

export interface Track {
  id: string
  title: string
  artist: string
  durationSec: number
  hue: Hue
  /** Object URL for a locally-added file. Not persisted — see storage notes. */
  url?: string
  /** Marks a piece intended for the audition submission. */
  audition?: boolean
}

export interface WikiPage {
  id: string
  title: string
  tag: string
  body: string
  updatedAt: number
}

export interface Note {
  id: string
  text: string
  hue: Hue
  createdAt: number
}

/* ----------------------------------------------------------------- settings */

export type ThemeMode = 'light' | 'dark' | 'system'

/** Which university intake the plan is built backwards from. */
export type IntakeId = 'aug2027' | 'feb2028'
export const INTAKES: { id: IntakeId; labelKey: string }[] = [
  { id: 'aug2027', labelKey: 'intake.aug2027' },
  { id: 'feb2028', labelKey: 'intake.feb2028' },
]

export type TileId =
  | 'nextUp'
  | 'countdown'
  | 'tasks'
  | 'studyWeek'
  | 'audition'
  | 'quickNote'
  | 'weather'
  | 'player'

export interface Settings {
  name: string
  city: string
  lang: 'en' | 'fa'
  targetIntake: IntakeId
  themeMode: ThemeMode
  accent: string
  /** Home tile order; ids absent from `hiddenTiles` render. */
  tileOrder: TileId[]
  hiddenTiles: TileId[]
  wideTiles: TileId[]
  /** Minutes for the focus / break phases of the study timer. */
  focusMins: number
  breakMins: number
}

/* -------------------------------------------------------------------- state */

export interface AppState {
  version: number
  settings: Settings
  events: CalEvent[]
  subjects: Subject[]
  tasks: Task[]
  sessions: StudySession[]
  applications: Application[]
  tracks: Track[]
  wiki: WikiPage[]
  notes: Note[]
  quickNote: string
}
